/**
 * In-Session Spacing Utilities
 *
 * The score-decay and DB-mutation methods that previously lived here have been
 * replaced by the Ebbinghaus model in scoreDecay.js (PRD §1.1).
 * Decay is now applied via applySessionDecay() inside adaptiveRoutes.js and
 * Ebbinghaus retention signals are computed in AdaptiveEngine.js.
 *
 * This file retains the in-session spacing helpers (HamTestOnline repetition
 * spacing) which are unrelated to long-term decay and remain valid.
 */

export class DecayCalculator {
  /**
   * Calculate in-session spacing for a question.
   * Ensures questions are spaced 8–15 positions apart within a single session.
   *
   * @param {Array} sessionQuestions - Questions answered so far this session
   * @param {Object} targetQuestion - Candidate question to check
   * @param {number} minGap - Minimum positions required between re-exposures
   * @param {number} maxGap - Maximum gap before spacing is considered fine regardless
   * @returns {{ canAsk: boolean, gap: number, lastPosition: number }}
   */
  calculateInSessionGap(sessionQuestions, targetQuestion, minGap = 8, maxGap = 15) {
    if (!sessionQuestions || sessionQuestions.length === 0) {
      return { canAsk: true, gap: 0 };
    }

    const lastIndex = sessionQuestions.length - 1;
    let lastOccurrence = -1;

    for (let i = lastIndex; i >= 0; i--) {
      const sessionQuestion = sessionQuestions[i];
      const questionId = sessionQuestion.questionId || sessionQuestion.id;
      if (questionId === targetQuestion.id) {
        lastOccurrence = i;
        break;
      }
    }

    if (lastOccurrence === -1) {
      return { canAsk: true, gap: sessionQuestions.length, lastPosition: -1 };
    }

    const gap = sessionQuestions.length - lastOccurrence - 1;
    const canAsk = gap >= minGap;

    return {
      canAsk,
      gap,
      lastPosition: lastOccurrence,
      minGapRequired: minGap,
      nextAvailablePosition: lastOccurrence + minGap,
    };
  }

  /**
   * Filter a candidate list to only questions that respect in-session spacing rules.
   *
   * @param {Array} candidates - Question candidates
   * @param {Array} sessionQuestions - Questions answered so far this session
   * @param {number} minGap - Minimum spacing between re-exposures
   * @returns {Array} Filtered candidates
   */
  filterByInSessionSpacing(candidates, sessionQuestions, minGap = 8) {
    if (!sessionQuestions || sessionQuestions.length === 0) {
      return candidates;
    }

    return candidates.filter(candidate => {
      const spacing = this.calculateInSessionGap(sessionQuestions, candidate, minGap);
      return spacing.canAsk;
    });
  }

  /**
   * Get spacing statistics for a session (useful for diagnostics/logging).
   *
   * @param {Array} sessionQuestions
   * @returns {{ totalQuestions, uniqueQuestions, repeatedQuestions, repetitionRate }}
   */
  getSessionSpacingStats(sessionQuestions) {
    if (!sessionQuestions || sessionQuestions.length === 0) {
      return { totalQuestions: 0, uniqueQuestions: 0, repeatedQuestions: [] };
    }

    const questionCounts = {};
    const repeated = [];

    for (const sessionQuestion of sessionQuestions) {
      const qId = sessionQuestion.questionId || sessionQuestion.id;
      questionCounts[qId] = (questionCounts[qId] || 0) + 1;

      if (questionCounts[qId] > 1) {
        repeated.push({ questionId: qId, occurrences: questionCounts[qId] });
      }
    }

    const uniqueCount = Object.keys(questionCounts).length;

    return {
      totalQuestions: sessionQuestions.length,
      uniqueQuestions: uniqueCount,
      repeatedQuestions: repeated,
      repetitionRate: uniqueCount > 0 ? (repeated.length / uniqueCount) * 100 : 0,
    };
  }
}

// Export singleton instance
export const decayCalculator = new DecayCalculator();
